import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, DimensionValue, Modal, TouchableOpacity, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import Map from '../../../components/Map';
import { centralAPI } from '../../../services/central-api';
import Toast from 'react-native-toast-message';
import { UseFormReturn } from 'react-hook-form';
import LocationSelector from '../../../components/LocationSelector';
import FormInput from '../../../components/FormInput';
import { applyMask } from '../../../components/helpers/documents';
import CityPropertyDropdown from '../../../components/app-agent/CityPropertyDropdown';
import { useAuth } from '../../../context/Auth';
import { useIsFocused } from '@react-navigation/native';
import { makeSetStateHandler } from '../../../components/helpers/form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    lastCep: string;
    setCep: (cep: string) => void;
    pin: LatLng | undefined;
    setPin: React.Dispatch<React.SetStateAction<LatLng | undefined>>;
    isEditing?: boolean;
}

export default function ActivitiesAndServicesLocationSelector({
    form,
    lastCep,
    setCep,
    pin,
    setPin,
    isEditing
}: Props) {
    const { styles, theme, breakpoint } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const items: { label: string; value: number; }[] = [
        {
            label: translate('currentLocation'),
            value: 0,
        },
        {
            label: translate('cityProperty'),
            value: 1,
        },
        {
            label: translate('street'),
            value: 2,
        },
        {
            label: translate('cep'),
            value: 3,
        },
        {
            label: translate('map'),
            value: 4,
        }
    ];

    const [resolvingLocation, setResolvingLocation] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<number>(isEditing ? 4 : 0);
    const [addCepManually, setAddCepManually] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    interface AddressByPlaceDetails {
        streetNumber?: string;
        streetName?: string;
        neighborhood?: string;
        city?: string;
        state?: string;
        country?: string;
        postalCode?: string;
        formattedAddress?: string;
        latitude?: number;
        longitude?: number;
    }

    const applyAddressDetails = useCallback((details: AddressByPlaceDetails) => {
        const streetAddress = details.streetName ?? details.formattedAddress;
        if (streetAddress) {
            form.setValue('address', streetAddress, { shouldValidate: true, shouldDirty: true });
        }
        if (details.postalCode) {
            const cleanPostalCode = details.postalCode.replace(/\D/g, '');
            setCep(cleanPostalCode);
            form.setValue('cep', cleanPostalCode, { shouldValidate: true, shouldDirty: true });
        }
        if (details.streetNumber) {
            form.setValue('number', details.streetNumber, { shouldValidate: true, shouldDirty: true });
        }
    }, [form]);

    const handlePinChange = useCallback(async (pos: LatLng) => {
        const current = form.getValues();
        if (current.latitude === pos.lat.toString() && current.longitude === pos.lng.toString()) {
            return;
        }

        setPin(pos);
        form.setValue('latitude', pos.lat.toString(), { shouldValidate: true, shouldDirty: true });
        form.setValue('longitude', pos.lng.toString(), { shouldValidate: true, shouldDirty: true });

        setResolvingLocation(true);
        setAddCepManually(false);
        try {
            const details = await centralAPI.getAddressByPlace({
                latitude: pos.lat,
                longitude: pos.lng,
                getFullObject: true,
            }) as AddressByPlaceDetails;

            if (details) {
                applyAddressDetails(details);
                if (!details.postalCode) {
                    setAddCepManually(true);
                    form.setValue('cep', '', { shouldValidate: true, shouldDirty: true });
                    Toast.show({ type: 'sentinelxWarning', text1: translate('cepMustBeAddedManually') });
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setResolvingLocation(false);
        }
    }, [form, applyAddressDetails]);

    window.sendLocation = (lat, lng) => {
        handlePinChange({ lat: Number(lat), lng: Number(lng) });
    };

    function getCurrentPosition() {
        if (window.AndroidBridge?.getLocation) {
            window.AndroidBridge?.getLocation();
        } else if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((pos) => {
                handlePinChange({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            });
        }
    }

    async function fetchLocationByCep(cep: string) {
        setResolvingLocation(true);
        try {
            const details = await centralAPI.getFullPlaceByAddress({
                address: cep,
                getFullObject: true,
            }) as AddressByPlaceDetails;

            if (!details || details.latitude === undefined || details.longitude === undefined) {
                Toast.show({ type: 'sentinelxWarning', text1: translate('cepNotFound') });
                return;
            }

            const pos = { lat: details.latitude, lng: details.longitude };
            setPin(pos);
            form.setValue('latitude', pos.lat.toString(), { shouldValidate: true, shouldDirty: true });
            form.setValue('longitude', pos.lng.toString(), { shouldValidate: true, shouldDirty: true });
            applyAddressDetails(details);
        } catch (err) {
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        } finally {
            setResolvingLocation(false);
        }
    }

    const cepDigits = (form.watch('cep') ?? '').replace(/\D/g, '');
    useEffect(() => {
        if (lastCep === cepDigits || addCepManually) {
            return;
        }

        if (cepDigits.length === 8) {
            fetchLocationByCep(cepDigits);
        }
    }, [cepDigits]);

    const isFocused = useIsFocused();
    useEffect(() => {
        if (!isEditing) {
            setSelectedItem(0);
            getCurrentPosition();
        } else {
            setSelectedItem(4);
        }
        form.setValue('cityPropertyId', 0);
    }, [isEditing, isFocused]);


    useEffect(() => {
        if (selectedItem === 0) {
            getCurrentPosition();
        }

        form.setValue('cityPropertyId', 0);
    }, [selectedItem]);

    return (
        <View style={{ flexDirection: 'column', gap: 10 }}>
            <View style={{ gap: 8 }}>
                <MyAppText style={styles.fieldsLabelStyle}>{translate('locationType')} <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText></MyAppText>
                <View
                    style={[
                        styles.itemsContainer,
                        breakpoint === 'md' && styles.itemsContainerTwoColumns,
                    ]}
                >
                    {
                        items.map((item) => {
                            const isSelected = selectedItem === item.value;
                            return (
                                <TouchableOpacity
                                    key={String(item.value)}
                                    style={[
                                        styles.row,
                                        breakpoint === 'md' && styles.rowTwoColumns,
                                    ]}
                                    onPress={() => setSelectedItem(isSelected ? 0 : item.value)}
                                >
                                    <View style={[styles.radio, isSelected && styles.radioSelected]}>
                                        {isSelected && <View style={styles.radioInner} />}
                                    </View>
                                    <MyAppText style={styles.label}>{item.label}</MyAppText>
                                </TouchableOpacity>
                            );
                        })
                    }
                </View>
            </View>
            {(selectedItem === 0 || selectedItem === 4) &&
                <TouchableOpacity onPress={() => selectedItem === 4 && setModalOpen(!modalOpen)}>
                    <View style={styles.mapContainer}>
                        <Map
                            pinnedMarker={pin}
                            draggable={false}
                            mapControls={false}
                        />
                        {resolvingLocation && (
                            <View style={styles.mapLoadingOverlay}>
                                <ActivityIndicator size='small' color={theme.colors.iconColor} />
                            </View>
                        )}
                    </View>
                </TouchableOpacity>
            }
            {selectedItem === 1 && authenticatedUser?.actingBodyUnit.actingBodyId && <View style={{ zIndex: 3 }}>
                <CityPropertyDropdown
                    value={form.getValues('cityPropertyId')}
                    setValue={makeSetStateHandler(form, 'cityPropertyId')}
                    actingBodyId={authenticatedUser?.actingBodyUnit.actingBodyId}
                    setPin={handlePinChange}
                    pin={pin}
                    zIndex={3}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView]}
                    required={selectedItem === 1}
                    hasNearLocation={(val) => {
                        if (!val) {
                            form.setValue('address', '');
                            form.setValue('cep', '');
                            form.setValue('number', '');
                            form.setValue('latitude', '');
                            form.setValue('longitude', '');
                        }
                    }}
                />
            </View>
            }

            <LocationSelector
                address={form.getValues('address')}
                onAddressChange={makeSetStateHandler(form, 'address')}
                onPinChange={(pos) => {
                    if (!pos) {
                        return;
                    }
                    handlePinChange(pos);
                }}
                viewStyle={[styles.itemView, { flex: 2, zIndex: 2 }]}
                labelStyle={styles.fieldsLabelStyle}
                label={translate('street')}
                required
                disabled={selectedItem != 2}
            />
            <View style={{ flexDirection: 'row', gap: 10 }}>
                <FormInput
                    form={form}
                    fieldName='cep'
                    customValidations={{
                        validCep: (val) => {
                            const cleanPostalCode = val.replace(/\D/g, '');
                            return (cleanPostalCode.length === 8 && /^\d{8}$/.test(cleanPostalCode)) || translate('invalid_field');
                        }
                    }}
                    label={translate('cep')}
                    maxLength={9}
                    mode='NUMBER'
                    mask={(val) => applyMask(val, 'cep')}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView, { flex: 2 }]}
                    required
                    disabled={!addCepManually && (resolvingLocation || selectedItem != 3)}
                />
                <FormInput
                    form={form}
                    fieldName='number'
                    label={translate('number')}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={styles.itemView}
                />
            </View>

            <Modal
                animationType='slide'
                transparent={true}
                visible={modalOpen}
                onRequestClose={() => {
                    setModalOpen(!modalOpen);
                }}
            >
                <View style={styles.mapModalView}>
                    <TouchableOpacity style={styles.modalBackgroundView} onPress={() => setModalOpen(!modalOpen)} />
                    <View style={styles.topModalView}>
                        <TouchableOpacity onPress={() => setModalOpen(!modalOpen)}>
                            <FontAwesomeIcon icon={faChevronDown} style={{
                                marginLeft: 10,
                                marginRight: 5,
                                color: theme.colors.secondaryButton.text
                            }} />
                        </TouchableOpacity>
                        <MyAppText style={styles.topModalText}>{translate('location')}</MyAppText>
                    </View>
                    <View style={{ width: '100%', height: '80%' }}>
                        <Map
                            dynamicMarker={pin}
                            useDynamicMarker={true}
                            draggable={selectedItem === 4}
                            markerChanged={(pos) => {
                                handlePinChange(pos);
                            }}
                            zoom={16}
                        />
                    </View>
                </View>
            </Modal>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: theme.colors.primary['500'],
    },
    radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary['500'],
    },
    label: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    mapContainer: {
        height: 200,
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        zIndex: 1
    },
    mapLoadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    itemView: {
        gap: 8,
        flex: 1
    },
    itemsContainer: {
        gap: 8,
    },
    itemsContainerTwoColumns: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 16,
        rowGap: 8,
    },
    rowTwoColumns: {
        width: '47%',
    },
    mapModalView: {
        flex: 1,
        justifyContent: 'flex-end',
    },
    modalBackgroundView: {
        height: `calc(20% - 50px)` as DimensionValue,
        width: '100%',
        borderColor: theme.colors.card.border,
        borderTopWidth: 1
    },
    topModalView: {
        height: 50,
        flexDirection: 'row',
        backgroundColor: theme.colors.card.background,
        alignItems: 'center'
    },
    topModalText: {
        color: theme.colors.neutral['500'],
        fontSize: 16
    }
}));
